import { useEffect, useState } from "react";

import styled from "styled-components/macro";

import extendedFieldDataTransformers from "ExtendedFields/dataTransformers";
import extendedFieldHooks from "ExtendedFields/selectorHooks";

import FnrFade from "UI/FnrFade/FnrFade";
import FnrInput2 from "UI/FnrInput2/FnrInput2";
import FnrHorizontalGap from "UI/commonContainers/FnrHorizontalGap/FnrHorizontalGap";
import FnrVerticalGap from "UI/commonContainers/FnrVerticalGap/FnrVerticalGap";

import ActiveInOtherGroups from "./ActiveInOtherGroups/ActiveInOtherGroups";
import AnotherGroupNotice from "./AnotherGroupNotice/AnotherGroupNotice";
import DeleteButton from "./DeleteButton/DeleteButton";
import ExtendedFieldsConfirmCancel from "./ExtendedFieldsConfirmCancel/ExtendedFieldsConfirmCancel";
import HeaderSwitch from "./HeaderSwitch/HeaderSwitch";
import { useFieldIsEditable } from "./hooks";
import ChangeCountry from "./inputs/ChangeCountry/ChangeCountry";
import IIG from "./inputs/IIG/IIG";
import Icon from "./inputs/Icon/Icon";
import IncludeYear from "./inputs/IncludeYear/IncludeYear";
import MinMax from "./inputs/MinMax/MinMax";
import Multiline from "./inputs/Multiline/Multiline";
import Options from "./inputs/Options/Options";
import PrivilegeLevel from "./inputs/PrivilegeLevel/PrivilegeLevel";
import RequiredField from "./inputs/RequiredField/RequiredField";
import ShareWith from "./inputs/ShareWith/ShareWith";
import ShowOnSignUp from "./inputs/ShowOnSignUp/ShowOnSignUp";

const { fieldTypes } = extendedFieldDataTransformers;

const IconAndTitleWrapper = styled(FnrHorizontalGap)`
  margin-top: 6px; /* Don't cut off the helper text! */
`;

const IconAndTitleSwitch = () => {
  const [title, setTitle] = extendedFieldHooks.useTitle();
  const isReadOnly = extendedFieldHooks.useIsReadOnly();
  // This is all commented out because the title wasn't changing when selecting a new field in the sidebar
  // const [isCustom] = extendedFieldHooks.useSelectedIsCustom();

  // this prevents the title from changing if it is about to be hidden.
  // const [frozenTitle, setFrozenTitle] = useState(title);

  const handleFocus = ({ target }) => {
    target.select();
  };

  return (
    <IconAndTitleWrapper topAlign gap={20} data-cy="IconAndTitleWrapper">
      <Icon />
      <FnrInput2
        value={title}
        onChange={({ target: { value } }) => {
          setTitle(value);
          // if (isCustom) setFrozenTitle(value);
        }}
        label="Profile Field Title"
        onFocus={handleFocus}
        borderColor="divider"
        fullWidth
        disabled={isReadOnly}
        data-cy="FieldEditor_TitleInput"
        charLimit={50}
      />
    </IconAndTitleWrapper>
  );
};

const CustomOptionSwitch = () => {
  const [fieldType] = extendedFieldHooks.useSelectedFieldType();
  const [isCustom] = extendedFieldHooks.useSelectedIsCustom();

  // this prevents the field type from changing if it is about to be hidden.
  const [frozenFieldType, setFrozenFieldType] = useState(fieldType);
  useEffect(() => {
    if (isCustom) setFrozenFieldType(fieldType);
  }, [isCustom, fieldType]);

  switch (frozenFieldType) {
    case fieldTypes.address:
      return <ChangeCountry />;
    case fieldTypes.date:
      return <IncludeYear />;
    case fieldTypes.number:
      return <MinMax />;
    case fieldTypes.text:
      return <Multiline />;
    case fieldTypes.selectlist:
      return <Options />;
    case fieldTypes.fileUpload:
    default:
      return null;
  }
};

const FieldEditor = () => {
  const [sharingIsBlocked] = extendedFieldHooks.useBlockSharing();
  const [isNewField] = extendedFieldHooks.useIsNewField();
  const [isAnotherGroup] = extendedFieldHooks.useSelectedIsAnotherGroup();
  const [showActiveOtherGroups] = extendedFieldHooks.useShowActiveOtherGroups();
  const [isForGroup] = extendedFieldHooks.useIsForGroup();
  const [isForSignupItem] = extendedFieldHooks.useIsForSignupItem();
  const [isCustom] = extendedFieldHooks.useSelectedIsCustom();
  const fieldIsEditable = useFieldIsEditable();
  const [showIIG] = extendedFieldHooks.useShowIIG();

  return (
    <div>
      {!isAnotherGroup && (
        <div data-cy="Screen_ExtendedFields_FieldEditor_Container">
          <FnrVerticalGap
            data-cy="ExtendedFields_FieldEditor_Container"
            gap={20}
          >
            {/* This is hidden for custom fields that are being edited. Except title is displayed for signups. */}
            <FnrFade show={!isCustom || isNewField || !fieldIsEditable} muted>
              <HeaderSwitch />
            </FnrFade>
            {fieldIsEditable && <IconAndTitleSwitch />}
            {fieldIsEditable && <CustomOptionSwitch />}
            {isForGroup && (
              <>
                <PrivilegeLevel />
                <ShowOnSignUp />
                <FnrFade show={!sharingIsBlocked} muted>
                  <ShareWith />
                </FnrFade>
                {showIIG && <IIG />}
              </>
            )}
            {isForSignupItem && <RequiredField />}
            <FnrHorizontalGap>
              <FnrFade show={!isNewField} muted>
                <DeleteButton />
              </FnrFade>
              <ExtendedFieldsConfirmCancel />
            </FnrHorizontalGap>
            <FnrFade show={showActiveOtherGroups} muted>
              <ActiveInOtherGroups />
            </FnrFade>
          </FnrVerticalGap>
        </div>
      )}
      {isForGroup && isAnotherGroup && (
        <div data-cy="Screen_AnotherGroupNotice">
          <AnotherGroupNotice />
        </div>
      )}
    </div>
  );
};

export default FieldEditor;
